import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';

import { MeQuery } from '../../../../../graphql/queries/account';
import {
  emailVerifyRequest,
  requestProAccess as requestProAccessMutation,
  clientStoreSet,
} from '../../../../../graphql/mutations/account';
import { getErrorString } from '../../../../../util/normalize-errors';

const withLandingPage = WrappedComponent => {
  class LandingPage extends Component {
    static propTypes = {
      completeOnboarding: PropTypes.func.isRequired,
      requestEmailVerification: PropTypes.func.isRequired,
      error: PropTypes.string,
      user: PropTypes.shape({
        id: PropTypes.string,
        email_verified: PropTypes.bool,
        active: PropTypes.bool,
        email_address: PropTypes.string,
      }),
      history: PropTypes.shape({
        replace: PropTypes.func.isRequired,
      }),
      requestProAccess: PropTypes.func.isRequired,
    };

    state = {
      emailSent: false,
      requestProAccessSuccess: false,
      requestProAccessError: '',
    };

    onDismiss = async basename => {
      const { completeOnboarding, user, history } = this.props;
      await completeOnboarding(user.id);
      history.replace(`/app/${basename}/folders`);
    };

    onRequestAccess = async () => {
      const { requestProAccess, user } = this.props;

      this.setState(() => ({ requestProAccessError: '' }));

      try {
        await requestProAccess(user.id);
        this.setState(() => ({ requestProAccessSuccess: true }));
      } catch (error) {
        this.setState(() => ({
          requestProAccessSuccess: false,
          requestProAccessError: getErrorString(error),
        }));
      }
    };

    resendEmailVerification = async event => {
      event.preventDefault();
      const { user, requestEmailVerification } = this.props;

      try {
        await requestEmailVerification(user.id);
        this.setState(() => ({ emailSent: true }));
      } catch ({ message }) {
        this.setState(() => ({
          emailSent: false,
        }));
      }
    };

    isEmailVerified = () => {
      const { user } = this.props;
      return user.email_verified;
    };

    isActive = () => {
      const { user } = this.props;
      return user.active;
    };

    // Unlocked users: Marketing activated (user.active) and email verified
    // (user.email_verified). Without these two conditions they cannot proceed
    // to the rest of the app.
    isUnlocked = () => {
      return this.isEmailVerified() && this.isActive();
    };

    hasRequestedProAccessBefore = () => {
      const { user } = this.props;

      return user?.onboarding?.data?.requested_pro_access;
    };

    render() {
      const { user } = this.props;

      if (!user) {
        return null;
      }

      return (
        <WrappedComponent
          {...this.props}
          resendEmailVerification={this.resendEmailVerification}
          onDismiss={this.onDismiss}
          emailSent={this.state.emailSent}
          error={this.props.error}
          isEmailVerified={this.isEmailVerified()}
          isActive={this.isActive()}
          isUnlocked={this.isUnlocked()}
          user={this.props.user}
          requestProAccessSuccess={this.state.requestProAccessSuccess}
          requestProAccessError={this.state.requestProAccessError}
          hasRequestedProAccessBefore={this.hasRequestedProAccessBefore()}
          onRequestAccess={this.onRequestAccess}
        />
      );
    }
  }

  const withUserData = graphql(MeQuery, {
    props: ({ data }) => ({
      loading: data.loading,
      error: data.error,
      user: data.me,
    }),
  });

  const withEmailVerifyRequestMutation = graphql(emailVerifyRequest, {
    props: ({ mutate }) => ({
      requestEmailVerification: accountId =>
        mutate({
          variables: {
            input: { account_id: accountId },
          },
        }),
    }),
  });

  const withUpdateOnboardingStateMutation = graphql(clientStoreSet, {
    props: ({ mutate }) => ({
      completeOnboarding: accountId =>
        mutate({
          variables: {
            input: {
              account_id: accountId,
              key: 'pro_landing',
              data: 'completed',
            },
          },
          update: store => {
            const data = store.readQuery({ query: MeQuery });

            store.writeQuery({
              query: MeQuery,
              data: {
                me: {
                  ...data.me,
                  onboarding: {
                    data: {
                      pro_landing: 'completed',
                      __typename: 'ClientStore',
                    },
                    __typename: 'ClientStore',
                  },
                },
              },
            });
          },
        }),
    }),
  });

  const withRequestProAccessMutation = graphql(requestProAccessMutation, {
    props: ({ mutate }) => ({
      requestProAccess: accountId =>
        mutate({
          variables: {
            input: { account_id: accountId },
          },
        }),
    }),
  });

  return compose(
    withRouter,
    withUserData,
    withEmailVerifyRequestMutation,
    withUpdateOnboardingStateMutation,
    withRequestProAccessMutation,
  )(LandingPage);
};

export default withLandingPage;
